import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import type {
  ERPAction,
  ERPState,
  MasterDataRecord,
  MrpRun,
  PlanningSuggestion,
  ProductionOrder,
  PurchaseOrder,
  PurchaseRequisition,
  SalesOrder,
} from './types';
import { erpApi, type PageQuery, type PageResult, type RemoteBom, type RemoteCustomerMaster, type RemoteItemMaster, type RemoteMrpRun, type RemoteProductionOrder, type RemotePurchaseOrder, type RemotePurchaseRequisition, type RemoteSalesOrder, type RemoteSuggestion, type RemoteSupplierMaster } from './erp.api';

export const initialState: ERPState = {
  salesOrders: [],
  mrpRuns: [],
  suggestions: [],
  purchaseRequisitions: [],
  purchaseOrders: [],
  productionOrders: [],
  masterData: [],
};

export function reducer(state: ERPState, action: ERPAction): ERPState {
  return action.type === 'SYNC_BACKEND_STATE' ? { ...state, ...action.state } : state;
}

const ERPContext = createContext<{
  state: ERPState;
  dispatch: React.Dispatch<ERPAction>;
  backend: { ready: boolean; error?: string; refresh: () => Promise<void> };
} | null>(null);

const ERP_LIST_PAGE_SIZE = 500;

async function loadAllPages<T>(loader: (query: PageQuery) => Promise<PageResult<T>>): Promise<PageResult<T>> {
  const firstPage = await loader({ page: 1, pageSize: ERP_LIST_PAGE_SIZE });
  const pageCount = Math.ceil(firstPage.total / ERP_LIST_PAGE_SIZE);
  if (pageCount <= 1) return firstPage;
  const remainingPages = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_, index) =>
      loader({ page: index + 2, pageSize: ERP_LIST_PAGE_SIZE }),
    ),
  );
  return { ...firstPage, items: [
    ...firstPage.items,
    ...remainingPages.flatMap(page => page.items),
  ] };
}

export const ERPProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [state, reducerDispatch] = useReducer(reducer, initialState);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string>();

  const syncBackendState = useCallback(async () => {
    const [orders, runs, remoteSuggestions, requisitions, purchaseOrders, productionOrders, customers, items, suppliers, boms] = await Promise.all([
      loadAllPages(erpApi.listSalesOrders),
      loadAllPages(erpApi.listMrpRuns),
      loadAllPages(erpApi.listSuggestions),
      loadAllPages(erpApi.listPurchaseRequisitions),
      loadAllPages(erpApi.listPurchaseOrders),
      loadAllPages(erpApi.listProductionOrders),
      loadAllPages(erpApi.listCustomerMasters),
      loadAllPages(erpApi.listItemMasters),
      loadAllPages(erpApi.listSupplierMasters),
      loadAllPages(erpApi.listBoms),
    ]);
    const orderMap = new Map(orders.items.map((order) => [order.uuid, order]));
    const runMap = new Map(runs.items.map((run) => [run.uuid, run]));
    const suggestionMap = new Map(remoteSuggestions.items.map((suggestion) => [suggestion.uuid, suggestion]));
    reducerDispatch({
      type: 'SYNC_BACKEND_STATE',
      state: {
        salesOrders: orders.items.map(mapSalesOrder),
        mrpRuns: runs.items.map((run) => mapMrpRun(run, remoteSuggestions.items)),
        suggestions: remoteSuggestions.items.map((suggestion) => mapSuggestion(suggestion, runMap, orderMap)),
        purchaseRequisitions: requisitions.items.map((requisition) => mapPurchaseRequisition(requisition, suggestionMap, runMap, orderMap)),
        purchaseOrders: purchaseOrders.items.map(mapPurchaseOrder),
        productionOrders: productionOrders.items.map((order) => mapProductionOrder(order, suggestionMap, orderMap)),
        masterData: [
          ...customers.items.map(mapCustomerMaster),
          ...items.items.map(mapItemMaster),
          ...suppliers.items.map(mapSupplierMaster),
          ...boms.items.map(mapBom),
        ],
      },
    });
  }, []);

  const refresh = useCallback(async () => {
    try {
      await syncBackendState();
      setError(undefined);
      setReady(true);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'ERP 后端暂不可用');
      setReady(false);
    }
  }, [syncBackendState]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const dispatch = useCallback<React.Dispatch<ERPAction>>((action) => {
    const execute = async () => {
      try {
        switch (action.type) {
          case 'CREATE_SALES_ORDER':
            await erpApi.createSalesOrder({
              customerCode: action.salesOrder.customerCode ?? `CUS-${action.salesOrder.customer.replace(/\s+/g, '-').slice(0, 48)}`,
              customerName: action.salesOrder.customer,
              lines: (action.salesOrder.lines?.length ? action.salesOrder.lines : [{
                id: action.salesOrder.id,
                itemCode: action.salesOrder.itemCode ?? action.salesOrder.product.replace(/\s+/g, '-').slice(0, 64),
                itemName: action.salesOrder.product,
                productVersion: action.salesOrder.productVersion,
                supplyType: action.salesOrder.supplyType ?? 'PRODUCTION',
                quantity: action.salesOrder.quantity,
                unit: action.salesOrder.unit ?? '套',
                unitPrice: action.salesOrder.amount / action.salesOrder.quantity || 0,
                requiredDate: action.salesOrder.deliveryDate,
              }]).map((line) => ({
                itemCode: line.itemCode,
                itemName: line.itemName,
                productVersion: line.productVersion,
                supplyType: line.supplyType,
                quantity: String(line.quantity),
                unit: line.unit,
                unitPrice: String(line.unitPrice),
                requiredDate: line.requiredDate,
              })),
            });
            await refresh();
            return;
          case 'CONFIRM_SALES_ORDER':
            await erpApi.confirmSalesOrder(action.salesOrderId);
            await erpApi.validateManufacturability(action.salesOrderId);
            await refresh();
            return;
          case 'RUN_MRP':
            await erpApi.createMrpRun(action.demandOrderUuids, action.engineeringBaselineSelections);
            await refresh();
            return;
          case 'REVIEW_SUGGESTION':
            await erpApi.approveSuggestion(action.suggestionId);
            await refresh();
            return;
          case 'CONVERT_SUGGESTION': {
            const suggestion = state.suggestions.find((item) => item.id === action.suggestionId);
            if (!suggestion) return;
            if (suggestion.type === '采购建议') {
              await erpApi.convertToPurchaseRequisition(action.suggestionId);
            } else {
              await erpApi.convertToProductionOrder(action.suggestionId);
            }
            await refresh();
            return;
          }
          case 'SUBMIT_PURCHASE_REQUISITION_TO_SRM':
            await erpApi.submitPurchaseRequisitionToSrm(action.requisitionId);
            await refresh();
            return;
          default:
            reducerDispatch(action);
        }
      } catch (requestError) {
        setError(requestError instanceof Error ? requestError.message : 'ERP 操作失败');
      }
    };
    void execute();
  }, [refresh, state.suggestions]);

  const value = useMemo(
    () => ({ state, dispatch, backend: { ready, error, refresh } }),
    [state, dispatch, ready, error, refresh],
  );

  return <ERPContext.Provider value={value}>{children}</ERPContext.Provider>;
};

const numeric = (value?: string) => Number(value || 0);

const mapSalesOrder = (order: RemoteSalesOrder): SalesOrder => {
  const line = order.lines[0];
  const quantity = order.lines.reduce((total, item) => total + numeric(item.quantity), 0);
  const amount = order.lines.reduce((total, item) => total + numeric(item.quantity) * numeric(item.unitPrice), 0);
  const stage = order.status === 'SHIPPED_G6'
    ? 'G6 已交付'
    : order.status === 'IN_FULFILLMENT'
      ? 'G3-G5 执行中'
      : order.status === 'PLANNED_G2'
        ? 'G2 计划生效'
        : order.status === 'MANUFACTURABLE_G1'
          ? 'G1 工程可制造'
          : 'G0 订单确认';
  return {
    id: order.uuid,
    code: order.orderCode,
    customer: order.customerName,
    product: order.lines.length > 1 ? `${line?.itemName || '—'} 等 ${order.lines.length} 项` : line?.itemName || '—',
    productVersion: order.lines.length > 1 ? '多 SKU' : line?.productVersion || '—',
    quantity,
    amount,
    deliveryDate: order.lines.length > 1 ? '详见订单行' : line?.requiredDate || '—',
    stage,
    status: order.status === 'DRAFT' ? '草稿' : order.status === 'CONFIRMED_G0' ? '待工程校验' : order.status === 'MANUFACTURABLE_G1' ? '待计划' : order.status === 'IN_FULFILLMENT' ? '履约执行中' : order.status === 'SHIPPED_G6' ? 'WMS 已发运' : '已生成经营单据',
    statusTone: order.status === 'DRAFT' ? 'default' : order.status === 'PLANNED_G2' || order.status === 'IN_FULFILLMENT' ? 'processing' : 'success',
    currency: order.currency,
    organization: '当前组织',
    customerCode: order.customerCode,
    itemCode: line?.itemCode,
    unit: line?.unit,
    supplyType: line?.supplyType,
    lines: order.lines.map(value => ({
      id: value.uuid,
      itemCode: value.itemCode,
      itemName: value.itemName,
      productVersion: value.productVersion,
      supplyType: value.supplyType,
      quantity: numeric(value.quantity),
      unit: value.unit,
      unitPrice: numeric(value.unitPrice),
      requiredDate: value.requiredDate,
    })),
  };
};

const mapMrpRun = (run: RemoteMrpRun, suggestions: RemoteSuggestion[]): MrpRun => ({
  id: run.uuid,
  code: run.runCode,
  period: run.inputSnapshotJson.demandOrderCodes?.join('、') || '历史计划范围',
  demandCount: run.inputSnapshotJson.demandOrderUuids?.length || 0,
  suggestionCount: suggestions.filter((suggestion) => suggestion.mrpRunUuid === run.uuid).length,
  exceptionCount: 0,
  status: run.status === 'COMPLETED' ? '待审核' : '草稿',
  createdAt: new Date(run.createdAt).toLocaleString('zh-CN', { hour12: false }),
  manufacturingDefinitions: run.inputSnapshotJson.engineeringBaselineSelections || [],
});

const mapSuggestion = (suggestion: RemoteSuggestion, runs: Map<string, RemoteMrpRun>, orders: Map<string, RemoteSalesOrder>): PlanningSuggestion => ({
  id: suggestion.uuid,
  code: suggestion.suggestionCode,
  mrpRunCode: runs.get(suggestion.mrpRunUuid)?.runCode || '—',
  type: suggestion.suggestionType === 'PURCHASE' ? '采购建议' : '生产建议',
  item: `${suggestion.itemName} ${suggestion.productVersion}`,
  quantity: numeric(suggestion.quantity),
  unit: suggestion.unit,
  requiredDate: suggestion.requiredDate,
  demandCode: orders.get(suggestion.sourceSalesOrderUuid)?.orderCode || '—',
  source: 'MRP 运行结果',
  status: suggestion.status === 'APPROVED' ? '已审核' : suggestion.status === 'CONVERTED_TO_PURCHASE_REQUISITION' ? '已转采购申请' : suggestion.status === 'CONVERTED_TO_PRODUCTION_ORDER' ? '已转生产订单' : '待审核',
  statusTone: suggestion.status === 'APPROVED' || suggestion.status.startsWith('CONVERTED') ? 'success' : 'warning',
});

const mapPurchaseRequisition = (requisition: RemotePurchaseRequisition, suggestions: Map<string, RemoteSuggestion>, runs: Map<string, RemoteMrpRun>, orders: Map<string, RemoteSalesOrder>): PurchaseRequisition => {
  const line = requisition.lines[0];
  const suggestion = suggestions.get(requisition.planningSuggestionUuid);
  return {
    id: requisition.uuid,
    code: requisition.requisitionCode,
    planningSuggestionId: requisition.planningSuggestionUuid,
    planningSuggestionCode: suggestion?.suggestionCode || '—',
    mrpRunCode: runs.get(requisition.mrpRunUuid)?.runCode || '—',
    demandCode: orders.get(requisition.sourceSalesOrderUuid)?.orderCode || '—',
    item: line?.itemName || '—',
    itemCode: line?.itemCode || '—',
    lineId: line?.uuid,
    quantity: numeric(line?.quantity),
    unit: line?.unit || '—',
    requiredDate: line?.requiredDate || '—',
    organization: '当前组织',
    applicant: '当前计划员',
    versionSnapshot: line?.productVersion || '—',
    source: 'MRP 运行结果',
    status: purchaseRequisitionStatusPresentation(requisition.status).status,
    statusTone: purchaseRequisitionStatusPresentation(requisition.status).statusTone,
    sourceStatus: requisition.status,
    sourcingSnapshot: requisition.sourcingSnapshot,
  };
};

const purchaseRequisitionStatusPresentation = (sourceStatus: string): Pick<PurchaseRequisition, 'status' | 'statusTone'> => {
  const presentation: Record<string, Pick<PurchaseRequisition, 'status' | 'statusTone'>> = {
    DRAFT: { status: '待提交 SRM', statusTone: 'default' },
    SOURCING_SUBMITTING: { status: '正在提交 SRM', statusTone: 'processing' },
    SOURCING_OPEN: { status: 'SRM 询价中', statusTone: 'processing' },
    SOURCING_RECOMMENDED: { status: 'SRM 已推荐供应商', statusTone: 'success' },
    SOURCING_NOT_ADOPTED: { status: 'SRM 未形成推荐', statusTone: 'warning' },
    SOURCING_SUBMISSION_FAILED: { status: 'SRM 下发失败', statusTone: 'error' },
  };
  return presentation[sourceStatus] ?? { status: '状态待同步确认', statusTone: 'warning' };
};

const mapPurchaseOrder = (order: RemotePurchaseOrder): PurchaseOrder => {
  const line = order.lines[0];
  const receivedQuantity = order.lines.reduce((sum, value) => sum + numeric(value.receivedQuantity), 0);
  const quantity = order.lines.reduce((sum, value) => sum + numeric(value.quantity), 0);
  return {
    id: order.uuid,
    code: order.purchaseOrderCode,
    supplier: `${order.supplierCode} · ${order.supplierName}`,
    item: order.lines.map(value => value.itemName).join('；') || '—',
    quantity,
    amount: 0,
    promisedDate: order.expectedDate,
    receiptResult: quantity ? `ERP 已登记 ${receivedQuantity} / ${quantity}` : '暂无明细',
    status: order.status === 'CLOSED' ? '已完成收货' : '待 WMS 收货',
    statusTone: order.status === 'CLOSED' ? 'success' : 'processing',
    sourceStatus: order.status,
    wmsInboundTask: order.wmsInboundTask
      ? {
          inboundOrderCode: order.wmsInboundTask.inboundOrderCode,
          warehouseUuid: order.wmsInboundTask.warehouseUuid,
          status: order.wmsInboundTask.status,
        }
      : undefined,
    requisitionCode: line?.purchaseRequisitionLineUuid ? '已关联采购申请' : undefined,
    receivedQuantity,
  };
};

const productionStatusPresentation: Record<string, Pick<ProductionOrder, 'status' | 'statusTone'>> = {
  DRAFT_PENDING_SCHEDULING: { status: '待下达 MES', statusTone: 'default' },
  COMPONENT_RESERVATION_REQUESTED: { status: '已提交 WMS 组件预留', statusTone: 'processing' },
  COMPONENTS_RESERVED: { status: '组件已由 WMS 预留', statusTone: 'success' },
  COMPONENT_SHORTAGE: { status: 'WMS 组件库存不足', statusTone: 'error' },
  COMPONENT_RESERVATION_RELEASED: { status: '组件预留已释放', statusTone: 'warning' },
  MES_RELEASED: { status: '已下达，等待 MES 接收', statusTone: 'processing' },
  MES_DISPATCH_FAILED: { status: 'MES 下达失败，待重试', statusTone: 'error' },
  MES_ACCEPTED: { status: 'MES 已接收，待开工', statusTone: 'processing' },
  MES_IN_PROGRESS: { status: '生产执行中', statusTone: 'processing' },
  PENDING_WMS_RECEIPT: { status: '已完工，等待成品入库', statusTone: 'warning' },
  WMS_RECEIVED: { status: '成品已入库', statusTone: 'success' },
  MATERIAL_PREPARING: { status: 'WMS 生产备料中', statusTone: 'processing' },
  MATERIAL_PREPARED: { status: '生产备料完成，待下达 MES', statusTone: 'success' },
  ECN_REPLAN_REQUIRED: { status: '工程变更，待重计划', statusTone: 'warning' },
};

const productionStatusOf = (sourceStatus: string): Pick<ProductionOrder, 'status' | 'statusTone'> =>
  productionStatusPresentation[sourceStatus] ?? { status: '状态待同步确认', statusTone: 'warning' };

const mapProductionOrder = (order: RemoteProductionOrder, suggestions: Map<string, RemoteSuggestion>, salesOrders: Map<string, RemoteSalesOrder>): ProductionOrder => {
  const line = order.lines[0];
  const suggestion = suggestions.get(order.planningSuggestionUuid);
  const statusPresentation = productionStatusOf(order.status);
  return {
    id: order.uuid,
    code: order.productionOrderCode,
    planningSuggestionId: order.planningSuggestionUuid,
    planningSuggestionCode: suggestion?.suggestionCode || '—',
    salesOrderCode: salesOrders.get(order.sourceSalesOrderUuid)?.orderCode || '—',
    product: line?.itemName || '—',
    version: order.snapshot ? `${order.snapshot.productVersion} / ${order.snapshot.mbomVersion}` : '—',
    quantity: numeric(line?.quantity),
    plannedDate: line?.requiredDate || '—',
    apsReference: '等待 APS 生效排程',
    mesResult: order.status === 'MES_ACCEPTED' ? 'MES 已接收，待派工' : order.status === 'MES_IN_PROGRESS' ? 'MES 生产执行中' : order.status === 'PENDING_WMS_RECEIPT' ? 'MES 已完工，等待 WMS 收货' : order.status === 'WMS_RECEIVED' ? 'MES 已完工，已回传 WMS 收货' : order.status === 'MES_RELEASED' ? '已下达，等待 MES 接收' : order.status === 'MES_DISPATCH_FAILED' ? 'MES 下达失败，可重试' : order.status === 'ECN_REPLAN_REQUIRED' ? '工程变更待重计划，MES 执行受控' : order.status === 'MATERIAL_PREPARED' ? 'WMS 已实物备料，可由 ERP 下达 MES' : order.status === 'MATERIAL_PREPARING' ? '等待 WMS 完成实物备料' : order.status === 'COMPONENTS_RESERVED' ? '库存已预留，等待 WMS 生产备料' : order.status === 'COMPONENT_SHORTAGE' ? '等待采购收货与质检放行后补充预留' : '等待 WMS 组件预留',
    qmsResult: order.mesDispatchSnapshot?.qmsProcessPlan ? `过程检验计划 ${order.mesDispatchSnapshot.qmsProcessPlan.planCode || '已引用'}` : '未配置过程检验',
    wmsResult: order.status === 'WMS_RECEIVED' ? 'WMS 已完成成品收货' : order.mesDispatchSnapshot?.warehouseUuid ? `成品收货仓 ${order.mesDispatchSnapshot.warehouseUuid}${order.mesDispatchSnapshot.receivingLocation ? ` / ${order.mesDispatchSnapshot.receivingLocation}` : ''}` : '待指定成品收货仓',
    ...statusPresentation,
    sourceStatus: order.status,
    mesExecutionOrderUuid: order.mesExecutionOrderUuid || undefined,
    actualMaterialConsumption: order.actualMaterialConsumption,
    componentReservation: order.componentReservation,
    shortageProcurement: order.shortageProcurement,
  };
};

const mapCustomerMaster = (customer: RemoteCustomerMaster): MasterDataRecord => ({
  id: customer.uuid,
  category: '客户',
  code: customer.customerCode,
  name: customer.customerName,
  version: '经营主数据',
  effectiveDate: '已生效',
  source: customer.sourceMode === 'ERP_MANAGED' ? 'ERP 自维护' : customer.sourceSystem || '外部接管',
  sourceMode: customer.sourceMode,
  status: customer.status === 'ACTIVE' ? '已启用' : '已停用',
  statusTone: customer.status === 'ACTIVE' ? 'success' : 'default',
});

const mapItemMaster = (item: RemoteItemMaster): MasterDataRecord => ({
  id: item.uuid,
  category: '物料',
  code: item.itemCode,
  name: item.itemName,
  version: item.productVersion,
  effectiveDate: '已生效',
  source: item.sourceMode === 'ERP_MANAGED' ? 'ERP 自维护' : item.sourceSystem || '外部接管',
  sourceMode: item.sourceMode,
  unit: item.unit,
  supplyType: item.supplyType,
  status: item.status === 'ACTIVE' ? '已启用' : '已停用',
  statusTone: item.status === 'ACTIVE' ? 'success' : 'default',
});

const mapSupplierMaster = (supplier: RemoteSupplierMaster): MasterDataRecord => ({
  id: supplier.uuid,
  category: '供应商',
  code: supplier.supplierCode,
  name: supplier.supplierName,
  version: '经营主数据',
  effectiveDate: '已生效',
  source: supplier.sourceMode === 'ERP_MANAGED' ? 'ERP 自维护' : supplier.sourceSystem || '外部接管',
  sourceMode: supplier.sourceMode,
  status: supplier.status === 'ACTIVE' ? '已启用' : '已停用',
  statusTone: supplier.status === 'ACTIVE' ? 'success' : 'default',
});

const mapBom = (bom: RemoteBom): MasterDataRecord => ({
  id: bom.uuid,
  category: 'BOM',
  code: bom.bomCode,
  name: `${bom.parentItemName} / ${bom.parentItemCode}`,
  version: `${bom.productVersion} / ${bom.bomVersion}`,
  effectiveDate: bom.effectiveDate,
  source: bom.sourceSystem === 'PLM' ? 'PLM 已发布 EBOM' : bom.sourceMode === 'ERP_MANAGED' ? 'ERP 自维护（历史兼容）' : '外部接管',
  sourceMode: bom.sourceMode,
  status: bom.status === 'RELEASED' ? '已发布' : bom.status === 'DRAFT' ? '草稿待发布' : '已停用',
  statusTone: bom.status === 'RELEASED' ? 'success' : bom.status === 'DRAFT' ? 'warning' : 'default',
  bomLines: bom.lines.map(line => ({ item: `${line.componentItemCode} · ${line.componentItemName}`, quantity: line.quantity, unit: line.unit })),
});

export const useERP = () => {
  const context = useContext(ERPContext);
  if (!context) {
    throw new Error('useERP must be used within ERPProvider');
  }
  return context;
};
